import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { kanbanStatsService } from '@/services/kanbanStatsService';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Progress } from '@/components/ui/Progress';
import { 
  BarChart3, 
  Users, 
  FileText, 
  DollarSign, 
  AlertCircle
} from 'lucide-react';

interface KanbanStatsTabProps {
  filters?: {
    companyId?: string;
    operadorId?: string;
    period?: string;
  };
}

export const KanbanStatsTab: React.FC<KanbanStatsTabProps> = ({ filters }) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['kanban-stats', filters],
    queryFn: () => kanbanStatsService.getKanbanStats(filters),
    refetchInterval: 30000,
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-8 bg-gray-200 rounded w-1/2"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          Erro ao carregar estatísticas
        </h3>
        <p className="text-gray-600 dark:text-gray-400">
          Não foi possível carregar as estatísticas dos kanbans.
        </p>
      </div>
    );
  }

  if (!data?.data) {
    return (
      <div className="text-center py-8">
        <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          Nenhum dado encontrado
        </h3>
        <p className="text-gray-600 dark:text-gray-400">
          Não há estatísticas disponíveis para os filtros selecionados.
        </p>
      </div>
    );
  }

  const { kanbans, resumoGeral } = data.data;

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('pt-BR').format(value);
  };

  return (
    <div className="space-y-6">
      {/* Resumo Geral */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Total de Kanbans
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {resumoGeral.totalKanbans}
                </p>
              </div>
              <BarChart3 className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Total de Títulos
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {formatNumber(resumoGeral.totalTitulos)}
                </p>
              </div>
              <FileText className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Valor Total
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {formatCurrency(resumoGeral.totalValor)}
                </p>
              </div>
              <DollarSign className="h-8 w-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Operadores Ativos
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {resumoGeral.totalOperadores}
                </p>
              </div>
              <Users className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Estatísticas por Kanban */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {kanbans.map((kanban: any) => (
          <Card key={kanban.id} className="relative">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <div 
                    className="w-4 h-4 rounded-full" 
                    style={{ backgroundColor: kanban.cor }}
                  />
                  {kanban.nome}
                </CardTitle>
                {kanban.isDefault && (
                  <Badge variant="secondary">Padrão</Badge>
                )}
              </div>
              {kanban.descricao && (
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {kanban.descricao}
                </p>
              )}
            </CardHeader>

            <CardContent className="space-y-4">
              {/* Estatísticas Principais */}
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {formatNumber(kanban.estatisticas.totalTitulos)}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Total de Títulos
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {formatCurrency(kanban.estatisticas.valorTotal)}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Valor Total
                  </p>
                </div>
              </div>

              {/* Taxa de Recuperação */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Taxa de Recuperação
                  </span>
                  <span className="text-sm font-bold text-gray-900 dark:text-white">
                    {kanban.estatisticas.taxaRecuperacao.toFixed(1)}%
                  </span>
                </div>
                <Progress 
                  value={kanban.estatisticas.taxaRecuperacao} 
                  className="h-2"
                />
              </div>

              {/* Status dos Títulos */}
              <div className="space-y-2">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                  Status dos Títulos
                </h4>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { key: 'titulosAbertos', label: 'Abertos' },
                    { key: 'titulosVencidos', label: 'Vencidos' },
                    { key: 'titulosParciais', label: 'Parciais' },
                    { key: 'titulosFechados', label: 'Fechados' },
                    ...(kanban.nome === 'Jurídico' ? [
                      { key: 'titulosCancelados', label: 'Cancelados' }
                    ] : [])
                  ].map(({ key, label }) => (
                    <div key={key} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          {label}
                        </span>
                      </div>
                      <Badge variant="outline">
                        {formatNumber(kanban.estatisticas[key as keyof typeof kanban.estatisticas] as number)}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>

              {/* Clientes Distintos por Status */}
              <div className="space-y-2">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                  Clientes Distintos por Status
                </h4>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { key: 'clientesAbertos', label: 'Abertos', tituloKey: 'titulosAbertos' },
                    { key: 'clientesVencidos', label: 'Vencidos', tituloKey: 'titulosVencidos' },
                    { key: 'clientesParciais', label: 'Parciais', tituloKey: 'titulosParciais' },
                    { key: 'clientesFechados', label: 'Fechados', tituloKey: 'titulosFechados' },
                    ...(kanban.nome === 'Jurídico' ? [
                      { key: 'clientesCancelados', label: 'Cancelados', tituloKey: 'titulosCancelados' }
                    ] : [])
                  ].map(({ key, label, tituloKey }) => {
                    const clientes = kanban.estatisticas[key as keyof typeof kanban.estatisticas] as number || 0;
                    const titulos = kanban.estatisticas[tituloKey as keyof typeof kanban.estatisticas] as number || 0;
                    return (
                      <div key={key} className="flex items-center justify-between p-2 bg-blue-50 dark:bg-blue-900/20 rounded">
                        <div className="flex flex-col gap-1">
                          <span className="text-xs text-gray-600 dark:text-gray-400">
                            {label}
                          </span>
                          <span className="text-xs text-gray-500 dark:text-gray-500">
                            {formatNumber(titulos)} títulos
                          </span>
                        </div>
                        <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                          {formatNumber(clientes)} clientes
                        </Badge>
                      </div>
                    );
                  })}
                </div>
                {/* Total de Clientes */}
                <div className="flex items-center justify-between p-2 bg-blue-100 dark:bg-blue-900/30 rounded border border-blue-200 dark:border-blue-800">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      Total de Clientes
                    </span>
                  </div>
                  <Badge className="bg-blue-200 text-blue-900 dark:bg-blue-800 dark:text-blue-100">
                    {formatNumber(kanban.estatisticas.totalClientes || 0)}
                  </Badge>
                </div>
              </div>

              {/* Valores por Status */}
              <div className="space-y-2">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                  Valores por Status
                </h4>
                <div className="space-y-1">
                  {[
                    { key: 'valorAberto', label: 'Abertos' },
                    { key: 'valorVencido', label: 'Vencidos' },
                    { key: 'valorParcial', label: 'Parciais' },
                    { key: 'valorFechado', label: 'Fechados' },
                    ...(kanban.nome === 'Jurídico' ? [
                      { key: 'valorCancelado', label: 'Cancelados' }
                    ] : [])
                  ].map(({ key, label }) => (
                    <div key={key} className="flex items-center justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">{label}</span>
                      <span className="font-medium text-gray-900 dark:text-white">
                        {formatCurrency(kanban.estatisticas[key as keyof typeof kanban.estatisticas] as number)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Operadores Ativos */}
              <div className="flex items-center justify-between p-2 bg-blue-50 dark:bg-blue-900/20 rounded">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-blue-500" />
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    Operadores Ativos
                  </span>
                </div>
                <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                  {kanban.estatisticas.operadoresAtivos}
                </Badge>
              </div>

              {/* Etapas */}
              {kanban.etapas.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                    Etapas ({kanban.etapas.length})
                  </h4>
                  <div className="space-y-1">
                    {kanban.etapas.map((etapa: any) => (
                      <div key={etapa.id} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded">
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-3 h-3 rounded-full" 
                            style={{ backgroundColor: etapa.cor }}
                          />
                          <div className="flex flex-col">
                            <span className="text-sm text-gray-600 dark:text-gray-400">
                              {etapa.nome}
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-500">
                              {formatNumber(etapa.clientesCount || 0)} clientes
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">
                            {formatNumber(etapa.titulosCount)}
                          </Badge>
                          <span className="text-xs text-gray-500">
                            {formatCurrency(etapa.valorTotal)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};
